import { computed, inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxComponentInputs,
  OnyxConfirmModalData,
  OnyxDropdownOptionsSource,
  OnyxFilter,
  OnyxIconName,
  OnyxMaybeArray,
  OnyxOptionsGroup,
  OnyxTooltipContext,
} from '@onyx/angular';
import {
  ActionHelper,
  DropdownHelper,
  OnyxConfirmModalComponent,
  OnyxFilterOperator,
  OnyxFormMode,
  OnyxModalService,
  OnyxNamePipe,
  OnyxToastService,
} from '@onyx/angular';
import { castArray, chain, isArray, isString, uniq } from 'lodash';
import type { Observable } from 'rxjs';
import { filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import type { PickDeep } from 'type-fest';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import type { TachographDriver } from '../../../../common/components/drivers-tachograph/drivers-tachograph.component';
import { IconTextTooltipComponent } from '../../../../common/components/tooltips/icon-text-tooltip/icon-text-tooltip.component';
import { UnavailabilityType } from '../../../../common/components/unavailabilities/unavailabilities.component';
import type { UnavailabilityModalData } from '../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { UnavailabilityModalComponent } from '../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { AdrClass } from '../../../../common/enums/adr-class';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationSeverity } from '../../../../common/enums/validation-severity';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../common/helpers/navigation.helper';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { HelperOptions } from '../../../../common/interfaces/utilities/helper-options';
import type { ValidationIssue } from '../../../../common/interfaces/validation/validation-issue';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { XlsxService } from '../../../common/services/xlsx.service';
import { DelegationStatus } from '../../../delegations/common/enums/delegation-status';
import { DelegationHelper } from '../../../delegations/common/helpers/delegation.helper';
import { DelegationsService } from '../../../delegations/common/services/delegations.service';
import { DelegationActionModalMode } from '../../../delegations/delegation-action-modal/delegation-action-modal.component';
import { TelematicsHelper } from '../../../map/common/helpers/telematics.helper';
import type { DriverModalData } from '../../driver-modal/driver-modal.component';
import { DriverModalComponent } from '../../driver-modal/driver-modal.component';
import type { DriverAssignedVehicleModalData } from '../components/driver-assigned-vehicle-modal/driver-assigned-vehicle-modal.component';
import { DriverAssignedVehicleModalComponent } from '../components/driver-assigned-vehicle-modal/driver-assigned-vehicle-modal.component';
import { DRIVERS_EXPORT_DECIMAL_PLACES } from '../constants/drivers-export-decimal-places';
import { getDriversExportData } from '../constants/drivers-export-schema';
import { DRIVERS_XLSX_TEMPLATE } from '../constants/drivers-xlsx-template';
import { DriversRoute } from '../enums/drivers-route';
import type { Driver, SimplifiedDriver } from '../interfaces/driver';
import { DriverCategory } from '../interfaces/driver-category';
import { DriverStatus } from '../interfaces/driver-status';
import { DriversService } from '../services/drivers.service';

export const DRIVER_HEADER_ACTIONS = [
  'share',
  'showOnGantt',
  'showOnMap',
] as const;

@Service()
export class DriverHelper {
  private readonly driversService = inject(DriversService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly modalService = inject(OnyxModalService);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );
  private readonly delegationsService = inject(DelegationsService);
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly xlsxService = inject(XlsxService);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly injector = inject(Injector);

  private readonly I18N = 'drivers.driversList';

  public hasModule = computed(() => this.authStore.hasModule('drivers'));

  public getDriversSource(
    options?: Parameters<DriversService['searchDrivers']>[2],
  ): OnyxDropdownOptionsSource<Driver> {
    return {
      list: (query, pagination) =>
        this.driversService.searchDrivers(
          query,
          {
            ...pagination,
            filters: [DriverHelper.createCategoryFilter(DriverCategory.ACTIVE)],
          },
          options,
        ),
      get: (uuid) =>
        this.driversService.getDriver(uuid).pipe(
          map((driver) => ({
            name: `${driver.driverData.firstName} ${driver.driverData.lastName}`,
            value: driver,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getOptions(
    driver: Driver,
    options: HelperOptions,
    exclude: readonly (typeof DRIVER_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const isArchived = DriverHelper.isArchived(driver);
    const hasActiveDelegation = DelegationHelper.isActive(driver.delegation);

    const issues: ValidationIssue[] = [];
    this.validationConditionsHelper.checkRequiredDrivingLicense(
      ValidationContext.DRIVER,
      driver,
      issues,
    );
    this.validationConditionsHelper.checkDrivingLicenseExpiryDate(
      ValidationContext.DRIVER,
      driver,
      issues,
    );

    return [
      {
        id: '0.default',
        options: [
          ...(!isArchived
            ? [
                {
                  name: `${this.translateService.instant('labels.edit')}...`,
                  value: () => this.edit(driver.uuid, options),
                  leftIcon: { name: 'edit-outlined', size: 20 },
                  ...(!!driver.relatedFleetSwap &&
                    DropdownHelper.getErrorFields(
                      'labels.disabledUntilSwapCompleted',
                    )),
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '1.actions',
        options: [
          ...(!exclude.includes('share')
            ? [
                {
                  name: 'labels.share',
                  value: () => this.share(driver),
                  leftIcon: { name: 'share-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnGantt')
            ? [
                {
                  name: 'labels.showOnGantt',
                  value: () => this.showOnGantt(driver, options),
                  leftIcon: { name: 'gantt-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnMap')
            ? [
                {
                  name: 'labels.showOnMap',
                  value: () => this.showOnMap(driver, options),
                  leftIcon: { name: 'map-view-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '2.options',
        options: [
          ...(!isArchived
            ? [
                ValidationHelper.mapIssues(
                  issues.filter(
                    (issue) => issue.severity === ValidationSeverity.ERROR,
                  ),
                  {
                    name: `${this.translateService.instant('labels.changeVehicle')}...`,
                    value: () =>
                      this.modalService.open<DriverAssignedVehicleModalData>(
                        DriverAssignedVehicleModalComponent,
                        driver,
                      ),
                    leftIcon: { name: 'add-outlined', size: 20 },
                    ...(hasActiveDelegation &&
                      DropdownHelper.getErrorFields(
                        'labels.driverHasActiveDelegation',
                      )),
                  } as const,
                ).option!,
              ]
            : []),
          ...(!isArchived
            ? [
                {
                  name: `${this.translateService.instant('unavailabilities.addUnavailability')}...`,
                  value: () =>
                    this.modalService.open<UnavailabilityModalData>(
                      UnavailabilityModalComponent,
                      {
                        mode: OnyxFormMode.ADD,
                        type: UnavailabilityType.DRIVER,
                        item: driver,
                      },
                    ),
                  leftIcon: { name: 'vacation-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      ...(!driver.delegation ||
      driver.delegation.status.value === DelegationStatus.ACTIVE
        ? [
            {
              id: '3.delegation',
              options: [
                ...(!driver.delegation
                  ? [
                      {
                        name: `${this.translateService.instant('labels.addDelegation')}...`,
                        value: () =>
                          DelegationHelper.add(
                            {
                              primaryDriverUuid: driver.uuid,
                              vehicleUuid: driver.assignedVehicle?.uuid,
                            },
                            this.router,
                          ),
                        leftIcon: { name: 'delegation-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
                ...(driver?.delegation?.status?.value ===
                DelegationStatus.ACTIVE
                  ? [
                      {
                        name: `${this.translateService.instant('labels.finishDelegation')}...`,
                        value: () =>
                          this.delegationsService
                            .getDelegation(driver.delegation!.uuid)
                            .subscribe((delegation) =>
                              this.delegationHelper.openActionModal(
                                DelegationActionModalMode.FINISH,
                                delegation,
                              ),
                            ),
                        leftIcon: { name: 'check-outlined', size: 20 },
                        ...(!!driver?.delegation.relatedFleetSwap &&
                          DropdownHelper.getErrorFields(
                            'labels.disabledUntilSwapCompleted',
                          )),
                      } as const,
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        id: '4.archive',
        options: [
          {
            name: 'labels.export',
            value: () => this.export(driver),
            leftIcon: { name: 'export-outlined', size: 20 },
          } as const,
          {
            name: isArchived ? 'labels.unarchive' : 'labels.archive',
            value: () => this.toggleArchived(driver, options),
            leftIcon: {
              name: isArchived ? 'undo-outlined' : 'archives-outlined',
              size: 20,
            },
            leftIconColor: isArchived ? 'black' : 'red',
            ...(hasActiveDelegation &&
              DropdownHelper.getErrorFields(
                `${this.I18N}.cannotArchiveDriverWithActiveDelegation`,
              )),
          } as const,
        ],
      },
    ].filter((group) => group.options.length !== 0);
  }

  public openModal(
    driver: DriverModalData['driver'],
    size: DriverModalData['size'] = 640,
  ): void {
    if (!this.hasModule()) return;

    this.modalService.open<DriverModalData>(DriverModalComponent, {
      driver,
      size,
    });
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      DriversRoute.EDIT_DRIVER.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public share(driver: SimplifiedDriver): void {
    ActionHelper.share(
      DriversRoute.DRIVER_CARD.replace(':uuid', driver.uuid),
      this.toastService,
    );
  }

  public showOnGantt(driver: Driver, options: HelperOptions): void {
    const vehicle = driver.assignedVehicle;
    if (!vehicle) return;

    CommonHelper.showOnGantt(this.router, 'vehicle', vehicle.uuid);
    CommonHelper.handleOptions(driver, options);
  }

  public showOnMap(driver: Driver, options: HelperOptions): void {
    const vehicle = driver.assignedVehicle;
    if (!vehicle) return;

    CommonHelper.showOnMap(this.router, vehicle.uuid, this.injector);
    CommonHelper.handleOptions(driver, options);
  }

  public export(data: OnyxMaybeArray<Driver>): void {
    this.xlsxService.export(
      {
        template: DRIVERS_XLSX_TEMPLATE,
        name: !isArray(data)
          ? this.namePipe.transform(data.driverData)
          : `dashboard.navBar.routes.${DriversRoute.DRIVERS_LIST}`,
      },
      getDriversExportData(data, this.injector),
      DRIVERS_EXPORT_DECIMAL_PLACES,
    );
  }

  public toggleArchived(
    drivers: OnyxMaybeArray<Driver>,
    options: HelperOptions,
    force?: boolean,
  ): void {
    const items = chain(drivers)
      .thru((drivers) => castArray(drivers))
      .groupBy((driver) => force ?? !DriverHelper.isArchived(driver))
      .thru((groups) => ({
        archive: groups['true'] ?? [],
        unarchive: groups['false'] ?? [],
      }))
      .value();
    const showConfirmModal = items.archive.some(
      (driver) => driver.assignedVehicle,
    );

    const toggle$ = (items: Driver[], force: boolean): Observable<void> => {
      if (!items.length) return of(undefined);

      return this.driversService
        .batchDrivers(
          items.map((item) => item.uuid),
          { ...(force ? { archive: true } : { unarchive: true }) },
        )
        .pipe(
          tap(() => {
            this.toastService.showSuccess({
              message: this.translateService.instant(
                `drivers.toasts.${items.length === 1 ? 'driver' : 'drivers'}${force ? 'Archived' : 'Unarchived'}`,
                { count: items.length },
              ),
            });
          }),
        );
    };
    const getName = (driver: Driver) => {
      const { firstName, lastName } = driver.driverData;
      return `${firstName} ${lastName}`;
    };

    const toggleAll$ = (groups: typeof items) =>
      forkJoin([
        toggle$(groups.archive, true),
        toggle$(groups.unarchive, false),
      ]);

    of(items)
      .pipe(
        switchMap((items) => {
          if (showConfirmModal) {
            return this.modalService
              .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
                heading: this.translateService.instant(
                  `${this.I18N}.sureToArchive${items.archive.length === 1 ? 'Driver' : 'Drivers'}`,
                  {
                    name: getName(items.archive[0]),
                    vehicle: this.fleetIdentifierPipe.transform(
                      items.archive[0].assignedVehicle,
                    ),
                  },
                ),
                badges:
                  items.archive.length > 1
                    ? items.archive.map((driver) => ({
                        value: getName(driver),
                        color: 'outlined-black',
                      }))
                    : undefined,
                message: `${this.I18N}.sureToContinueArchive`,
                confirmButtonText: 'labels.archive',
                action: () => toggleAll$(items),
              })
              .result.pipe(filter((ok) => !!ok));
          }
          return toggleAll$(items);
        }),
      )
      .subscribe(() => CommonHelper.handleOptions(drivers, options));
  }

  public static getSelectedDriverTooltip(
    driver: TachographDriver,
    namePipe: OnyxNamePipe | null,
  ): OnyxTooltipContext {
    const workingMode = driver.tachograph?.workingMode;
    return {
      ref: IconTextTooltipComponent,
      inputs: {
        icon: { name: TelematicsHelper.getDriverIcon(driver), size: 20 },
        heading: workingMode
          ? `${DictionaryCode.TACHOGRAPH_WORKING_MODE}.${workingMode}`
          : `components.driversTachograph.${driver.telematics ? 'connectionWaiting' : 'noIntegration'}`,
        ...(namePipe && { description: namePipe.transform(driver.driverData) }),
      } satisfies OnyxComponentInputs<IconTextTooltipComponent>,
    };
  }

  public static getDriverAdrClasses(
    driver: Driver,
    vehicleType: string | null,
  ): string[] {
    const REQUIREMENTS_ADR = [
      AdrClass.ADR_2_1,
      AdrClass.ADR_2_2,
      AdrClass.ADR_2_3,
      AdrClass.ADR_3,
      AdrClass.ADR_4_1,
      AdrClass.ADR_4_2,
      AdrClass.ADR_4_3,
      AdrClass.ADR_5_1,
      AdrClass.ADR_5_2,
      AdrClass.ADR_6_1,
      AdrClass.ADR_6_2,
      AdrClass.ADR_8,
      AdrClass.ADR_9,
    ];

    const isFuelTanker = vehicleType === 'fuel-tanker';
    const adrClassMap: Partial<Record<OnyxIconName, AdrClass[]>> = {
      [AdrClass.ADR_1]: [AdrClass.ADR_1],
      [AdrClass.ADR_7]: [AdrClass.ADR_7],
      'requirements-adr-outlined': !isFuelTanker ? REQUIREMENTS_ADR : [],
      'requirements-adr-tank-outlined': isFuelTanker ? REQUIREMENTS_ADR : [],
    };

    return uniq(
      driver.permissions.flatMap((permission) => {
        const icon = permission.type.icon;
        if (!icon) return [];

        return adrClassMap[icon] ?? [];
      }),
    );
  }

  public static isActive(category: DriverCategory): boolean {
    return category === DriverCategory.ACTIVE;
  }

  public static isArchived(driver: Driver | DriverStatus): boolean {
    const status = isString(driver) ? driver : driver.status.status;
    return status === DriverStatus.ARCHIVED;
  }

  public static getDriverExpiryDocuments(
    driver: PickDeep<
      Driver,
      | 'driverCard'
      | 'identityDocuments'
      | 'identityDocuments.identityDocument'
      | 'identityDocuments.polishCard'
      | 'identityDocuments.passport'
      | 'identityDocuments.residenceCard'
    >,
  ) {
    return [
      {
        key: 'driverCard',
        expirationDate: driver.driverCard?.expirationDate,
      },
      {
        key: 'identityDocument',
        expirationDate:
          driver.identityDocuments.identityDocument?.expirationDate,
      },
      {
        key: 'polishCard',
        expirationDate: driver.identityDocuments.polishCard?.expirationDate,
      },
      {
        key: 'passport',
        expirationDate: driver.identityDocuments.passport?.expirationDate,
      },
      {
        key: 'residenceCard',
        expirationDate: driver.identityDocuments.residenceCard?.expirationDate,
      },
    ];
  }

  public static isDriverType(
    driver: SimplifiedDriver | Driver,
  ): driver is Driver {
    return 'employmentConditions' in driver;
  }

  public static createCategoryFilter(category: DriverCategory): OnyxFilter {
    return {
      key: 'category',
      operator: OnyxFilterOperator.IN,
      value: [category],
    };
  }

  public static createStatusFilter(statuses: DriverStatus[]): OnyxFilter {
    return { key: 'status', operator: OnyxFilterOperator.IN, value: statuses };
  }
}
